import { Box, Button, Card, IconButton, InputBaseComponentProps, TextField, Typography } from "@mui/material";
import { InputContainer } from "shared/styles/input-container-style";
import { forwardRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { ShowMoreNoteDialog } from "./show-more-notefield";
import { useDialog } from "shared/dialog/use-dialog";
import { useTranslation } from "react-i18next";
import { ScreenSizeEnum } from "shared/use-screen-size";
import { useRef, useState, useLayoutEffect } from "react";

export interface NoteFieldProps {
  value?: string | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  showMoreLabel?: string; // label for the show more button
  disableEdit?: boolean; // disables editing
  disableButton?: boolean; // disables the button
  inputProps?: InputBaseComponentProps;
  onButtonClick?: () => void;
  noContentText?: string;
  screenSize: ScreenSizeEnum;
  canEditNote: boolean; // if true, the note can be edited
  inADialog?: boolean; // if true, the note is displayed in a dialog
  addTopPadding?: boolean; // if true, adds top padding to the card
  minHeight?: string; // minimum height of the text field
  textSize?: string; // size of the text in the text field
  textColor?: string; // color of the text in the text field
  disablePadding?: boolean; // if true, disables padding around the card
  labelVariant?: "caption" | "body1" | "body2" | "h6" | "h5" | "h4" | "h3" | "h2" | "h1"; // size of the title text, default is body1
  labelWeight?: "bold" | "normal"; // weight of the title text, default is bold
  borderRadius?: string; // border radius of the card, default is 10px
  lineClamp?: number; // number of lines to clamp the text to when not editing
}

export const NoteField = forwardRef<HTMLDivElement, NoteFieldProps>((props, ref) => {
  const { t } = useTranslation();
  const padLeft = "30px";
  const padRight = "30px";
  const padTop = !props.addTopPadding ? "30px" : "0px";
  const padBottom = "20px";

  const [openDialog] = useDialog(ShowMoreNoteDialog);

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const showMoreActionClicked = () => {
    if (props.value) {
      openDialog({ stringValue: props.value, header: props.showMoreLabel || t("common.note") });
    }
  };

  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight || textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [props.value, props.screenSize]);

  return (
    <Box sx={{ width: "auto", maxWidth: "auto", minHeight: props.minHeight ? props.minHeight : "210px" }}>
      <Card
        ref={ref}
        sx={{
          borderStyle: "none",
          position: "relative",
          pl: !props.disablePadding ? padLeft : 0,
          pr: !props.disablePadding ? padRight : 0,
          pb: !props.disablePadding ? padBottom : 0,
          pt: !props.disablePadding ? padTop : 0,
          borderRadius: props.borderRadius || "10px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" height="auto" sx={{ pb: padBottom }}>
          {props.label ? (
            <Typography justifySelf="center" variant={props.labelVariant || "body1"} color={"primary.main"} fontWeight={"bold"}>
              {props.label}
            </Typography>
          ) : null}

          {!props.disableButton && props.canEditNote && (
            <IconButton onClick={props.onButtonClick} sx={{ padding: 0, color: "primary.main" }}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <InputContainer sx={{ backgroundColor: "#fff" }}>
          {props.disableEdit || !props.canEditNote ? (
            <>
              {props.value ? (
                <Typography
                  variant="body1"
                  ref={textRef}
                  whiteSpace="pre-wrap"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: props.lineClamp || 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: props.textSize || "16px",
                    color: props.textColor || "primary.main",
                  }}
                >

                  {props.value}

                </Typography>
              ) : (
                <Typography variant="body1" color="grey.100">
                  {props.noContentText}
                </Typography>
              )}
            </>
          ) : props.inADialog ? (
            <TextField value={props.value ?? ""} onChange={handleNoteChange} variant="filled" multiline={true} maxRows={15} minRows={3} />
          ) : (
            <TextField
              value={props.value ?? ""}
              onChange={handleNoteChange}
              variant="filled"
              multiline={true}
              minRows={3}
              maxRows={12}
              inputProps={{
                ...props.inputProps,
                style: {
                  ...props.inputProps?.style,
                  minHeight: "32px",
                  maxHeight: "150px",
                  overflow: "auto",
                  resize: "none",
                },
              }}
            />
          )}
        </InputContainer>
      </Card>
      {props.value && !props.inADialog && isOverflowing && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="text" color="primary" onClick={showMoreActionClicked} sx={{ pr: padRight }}>
            {t("common.viewMore")}
          </Button>
        </Box>
      )}
    </Box>
  );
});

NoteField.displayName = "NoteField";
